// The site filter of the header (UIC-3): "all sites" or one site. One answer
// for the whole shell - the summary strip and the overview read it, and the
// other screens will (UIC-5 puts it in the address as ?site=). Remembered in
// this browser; a stored site that no longer exists reads as "all".

import { defineStore } from 'pinia'
import { computed, shallowRef } from 'vue'
import type { ID } from '@/api/types'
import { useLinkStore } from './link'

export const SITE_FILTER_STORAGE_KEY = 'airgate.siteFilter'

function readStored(): ID | null {
  try {
    const v = window.localStorage.getItem(SITE_FILTER_STORAGE_KEY)
    return v ? v : null
  } catch {
    return null
  }
}

function writeStored(id: ID | null): void {
  try {
    if (id) window.localStorage.setItem(SITE_FILTER_STORAGE_KEY, id)
    else window.localStorage.removeItem(SITE_FILTER_STORAGE_KEY)
  } catch {
    // A per-viewer convenience, never load-bearing.
  }
}

export const useSiteFilterStore = defineStore('console-site-filter', () => {
  const link = useLinkStore()
  const chosen = shallowRef<ID | null>(readStored())

  /** The site the shell is narrowed to; null = all. Falls back to null once the site list is known and lacks the stored id. */
  const siteId = computed<ID | null>(() => {
    const id = chosen.value
    if (!id) return null
    // Before the site list arrives keep the choice (no flash of "all").
    if (link.sites.length === 0) return id
    return link.sites.some((s) => s.id === id) ? id : null
  })

  function select(id: ID | null): void {
    chosen.value = id
    writeStored(id)
  }

  /** True when a record of this site is inside the filter. */
  function includes(site: ID): boolean {
    return siteId.value === null || siteId.value === site
  }

  return { siteId, select, includes }
})
